import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { REASON_MAX } from './hrApi'
import { adjustLeaveBalance, type LeaveType } from './leaveApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface Props {
  open: boolean
  personId: string
  year: number
  /** ⚠️ FAQAT balansdan yechiladigan turlar — quyidagi izohga qarang */
  types: readonly LeaveType[]
  onClose: () => void
}

interface FormValues {
  leaveTypeId: string
  days: number
  reason: string
}

/**
 * Ta'til balansini qo'lda tuzatish.
 *
 * <h2>⚠️ NEGA BU YO'L UMUMAN BOR</h2>
 * Ta'til huquqini pro-rata bo'lish ATAYLAB yozilmagan
 * (`xsib-hr/README.md`): qoida mehnat qonunchiligidan keladi va
 * Afg'oniston bilan O'zbekistonda u bir xil emas. Bugun huquq to'liq
 * beriladi, farqi esa SHU YERDA qo'lda tuzatiladi — ya'ni bu vaqtinchalik
 * emas, ONGLI yechim va u ko'rinadigan iz qoldiradi.
 *
 * <h2>⚠️ TUZATISH USTIGA YOZILMAYDI</h2>
 * Qatorlar faqat QO'SHILADI (`hr.append_only()`). Xato tuzatish teskari
 * ishorali yangi qator bilan to'g'rilanadi, o'chirilmaydi — «balans
 * nega shunday» degan savol javobsiz qolmasin.
 *
 * <h2>⚠️ NOL QABUL QILINMAYDI</h2>
 * Server ham rad etadi: musbat — qo'shish, manfiy — ayirish, nol esa
 * hech narsa. Nolni yozish sababi bor «bo'sh» qator yaratardi.
 *
 * <h2>⚠️ TANLOVDA BALANSLI TURLAR</h2>
 * Cheklovsiz turda (`usesBalance = false`) qoldiq tushunchasi yo'q,
 * ya'ni tuzatish hech narsani o'zgartirmasdi — lekin qator yozilardi.
 */
export function LeaveAdjustModal({ open, personId, year, types, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({ leaveTypeId: undefined, days: 1, reason: '' })
  }, [open, form])

  const adjust = useMutation({
    mutationFn: (values: FormValues) =>
      adjustLeaveBalance(personId, {
        leaveTypeId: values.leaveTypeId,
        year,
        days: values.days,
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    adjust.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.leave.adjustTitle', { year })}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => adjust.mutate(values))}
      okButtonProps={{ disabled: types.length === 0 }}
      confirmLoading={adjust.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.leave.adjustNote')} fontSize={13} />
      </div>

      {adjust.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(adjust.error, t)}
        />
      ) : null}

      {types.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.leave.noBalanceTypes')} />
      ) : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={adjust.isPending}>
          <Form.Item
            name="leaveTypeId"
            label={t('hr.leave.type')}
            rules={[{ required: true, message: t('hr.leave.validation.typeRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('hr.leave.typePlaceholder')}
              options={types.map((type) => ({ value: type.id, label: type.name }))}
            />
          </Form.Item>

          <Form.Item
            name="days"
            label={t('hr.leave.adjustDays')}
            extra={t('hr.leave.adjustDaysHint')}
            rules={[
              {
                validator: (_rule, value: number | null) =>
                  typeof value === 'number' && value !== 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('hr.leave.validation.adjustDaysRequired'))),
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} precision={0} autoFocus />
          </Form.Item>

          <Form.Item
            name="reason"
            label={t('hr.leave.reason')}
            extra={t('hr.leave.adjustReasonHint')}
            rules={[
              { required: true, message: t('hr.salary.validation.reasonRequired') },
              { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
            ]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
